import { noop } from 'lodash';
import { Subscription } from 'rxjs';
import {
    ApolloLink,
    FetchResult,
    NextLink,
    Observable,
    Observer,
    Operation,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { bachPinboardSessionKeyQuery } from '../bach/bach.util';
import { getSessionFromCache } from '../stateful/stateful-service-hooks.util';

interface QueuedOperation {
    operation: Operation;
    forward: NextLink;
    observer: Observer<FetchResult>;
    canceled: boolean;
    subscription?: Subscription;
}

interface SerializationContext {
    serializeMutation?: {
        sessionId: string;
        mutationId?: string;
        fetchOptions?: { signal?: AbortSignal };
    };
    [key: string]: any;
}

export class PinboardSerializingLink extends ApolloLink {
    private operationQueues: Map<string, QueuedOperation[]> = new Map();

    private activeSessions: Set<string> = new Set();

    public request(
        operation: Operation,
        forward: NextLink,
    ): Observable<FetchResult> {
        const context = operation.getContext() as SerializationContext;
        const serializationInfo = context.serializeMutation;

        const operationType = (operation.query.definitions.find(
            def => def.kind === 'OperationDefinition',
        ) as any)?.operation;

        if (
            operationType === 'mutation' &&
            serializationInfo?.sessionId &&
            !context?.fetchOptions?.signal?.aborted
        ) {
            const sessionId = serializationInfo.sessionId;

            return new Observable<FetchResult>(
                (observer: Observer<FetchResult>) => {
                    const queueEntry: QueuedOperation = {
                        operation,
                        forward,
                        observer,
                        canceled: false,
                    };

                    const queue = this.operationQueues.get(sessionId) || [];
                    queue.push(queueEntry);
                    this.operationQueues.set(sessionId, queue);

                    this.tryProcessQueue(sessionId);

                    return () => {
                        queueEntry.canceled = true;
                        if (queueEntry.subscription) {
                            queueEntry.subscription.unsubscribe();
                        }
                        this.handleOperationCompletion(sessionId);
                    };
                },
            );
        }

        return forward(operation);
    }

    private tryProcessQueue(sessionId: string): void {
        if (this.activeSessions.has(sessionId)) return;

        const queue = this.operationQueues.get(sessionId);
        if (!queue || queue.length === 0) {
            this.operationQueues.delete(sessionId);
            this.activeSessions.delete(sessionId);
            return;
        }

        while (queue.length && queue[0].canceled) {
            queue.shift();
        }

        if (queue.length === 0) {
            this.operationQueues.delete(sessionId);
            this.activeSessions.delete(sessionId);
            return;
        }

        const nextEntry = queue[0]; // Peek (don't shift yet)
        const { operation, forward, observer } = nextEntry;
        // update the variables with the updated session
        operation.variables = {
            ...operation.variables,
            session: {
                ...operation.variables?.session,
                ...getSessionFromCache(
                    operation.getContext().cache,
                    sessionId,
                    'BachPinboardSession',
                    bachPinboardSessionKeyQuery,
                    ['contextBookSessions'],
                ),
            },
        };

        this.activeSessions.add(sessionId);

        const signal: AbortSignal | undefined = operation.getContext()
            ?.fetchOptions?.signal;

        let cleanupAbort: () => void = noop;

        if (signal?.addEventListener) {
            const onAbort = () => {
                if (!nextEntry.canceled) {
                    nextEntry.canceled = true;
                    observer.complete();
                    this.handleOperationCompletion(sessionId);
                }
            };
            signal.addEventListener('abort', onAbort, { once: true });

            cleanupAbort = () => {
                signal.removeEventListener('abort', onAbort);
            };
        }

        const subscription = forward(operation).subscribe({
            next: (result: any) => {
                if (!nextEntry.canceled) {
                    observer.next(result);
                }
            },
            error: (error: any) => {
                if (!nextEntry.canceled) {
                    observer.error(error);
                }
                cleanupAbort();
                this.handleOperationCompletion(sessionId);
            },
            complete: () => {
                if (!nextEntry.canceled) {
                    observer.complete();
                }
                cleanupAbort();
                this.handleOperationCompletion(sessionId);
            },
        });

        nextEntry.subscription = subscription;
    }

    private handleOperationCompletion(sessionId: string): void {
        const queue = this.operationQueues.get(sessionId);
        if (queue?.length) {
            queue.shift();
            this.operationQueues.set(sessionId, queue);
        }

        this.activeSessions.delete(sessionId);
        this.tryProcessQueue(sessionId);
    }
}
